import React, { useState, useRef } from 'react'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Uploader } from '@shared/uploader'
import { wrap, link, block } from '@react/helpers/markdown_format'
import { postComment, fileLinks } from '../api'
import { RETRY_COMMENT } from '../helpers'

const RETRY_FILE = 'Ajoutez le fichier de nouveau pour l’envoyer.'

const codeBlock = (text, start, end) => {
  const fenced = `\`\`\`\n${text.slice(start, end)}\n\`\`\``
  return { text: text.slice(0, start) + fenced + text.slice(end), start: start + 4, end: start + 4 + (end - start) }
}

// The ticket composer's formats, in Slack's order; groups are split by a rule.
const FORMATS = [
  [
    { key: 'bold',   icon: 'bold',          label: 'Gras',     shortcut: 'b', run: (t, s, e) => wrap(t, s, e, '**') },
    { key: 'italic', icon: 'italic',        label: 'Italique', shortcut: 'i', run: (t, s, e) => wrap(t, s, e, '_') },
    { key: 'strike', icon: 'strikethrough', label: 'Barré',    run: (t, s, e) => wrap(t, s, e, '~~') }
  ],
  [
    { key: 'link',    icon: 'link',    label: 'Lien',            shortcut: 'k', run: link },
    { key: 'ordered', icon: 'list-ol', label: 'Liste numérotée', run: (t, s, e) => block(t, s, e, 'ordered') },
    { key: 'bullet',  icon: 'list-ul', label: 'Liste',           run: (t, s, e) => block(t, s, e, 'bullet') }
  ],
  [
    { key: 'quote', icon: 'quote-right', label: 'Citation',       run: (t, s, e) => block(t, s, e, 'quote') },
    { key: 'code',  icon: 'code',        label: 'Code',           run: (t, s, e) => wrap(t, s, e, '`') },
    { key: 'block', icon: 'file-code',   label: 'Bloc de code',   run: codeBlock }
  ]
]

const SHORTCUT = navigator.platform.includes('Mac') ? '⌘' : 'Ctrl+'

// The ticket composer's icon button (.slack-composer__icon).
const ICON = 'm-0 flex h-[28px] w-[28px] cursor-pointer items-center justify-center rounded border-0 bg-white/0 p-0 text-[14px] text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none'

const CommentForm = ({ commentPath, filesPath, number, currentName, onPosted }) => {
  const [body,      setBody]      = useState('')
  const [uploading, setUploading] = useState(false)
  const [linking,   setLinking]   = useState(false)
  const [posting,   setPosting]   = useState(false)
  const [notice,    setNotice]    = useState(null)
  const textarea = useRef(null)
  const uploader = useRef(null)
  const busy     = uploading || linking

  const format = (run) => {
    const el = textarea.current
    if (!el) return
    const result = run(el.value, el.selectionStart, el.selectionEnd)
    setBody(result.text)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(result.start, result.end)
    })
  }

  // Each file lands where the cursor is, as GitHub does: an image shows inline.
  const insert = (markdown) => {
    const el    = textarea.current
    const start = el ? el.selectionStart : body.length
    const end   = el ? el.selectionEnd : body.length
    setBody(current => {
      const before = current.slice(0, start)
      const pad    = before && !before.endsWith('\n') ? '\n' : ''
      return `${before}${pad}${markdown}\n${current.slice(end)}`
    })
  }

  const onUpload = (signedIds) => {
    setLinking(true)
    setNotice(null)
    fileLinks(filesPath, signedIds)
      .then(data => {
        if (!data || !data.files) return setNotice(RETRY_FILE)
        insert(data.files.map(f => `${f.image ? '!' : ''}[${f.name}](${f.url})`).join('\n'))
      })
      .catch(() => setNotice(RETRY_FILE))
      .finally(() => setLinking(false))
  }

  const submit = (e) => {
    e?.preventDefault()
    if (posting || busy || body.trim() === '') return

    setPosting(true)
    setNotice(null)
    postComment(commentPath, { number, body })
      .then(comment => {
        if (comment && comment.error) return setNotice(comment.error)
        setBody('')
        onPosted(comment)
      })
      .catch(() => setNotice(RETRY_COMMENT))
      .finally(() => setPosting(false))
  }

  const onKeyDown = (e) => {
    if (!(e.metaKey || e.ctrlKey)) return
    if (e.key === 'Enter') {
      e.preventDefault()
      return submit()
    }
    const match = FORMATS.flat().find(f => f.shortcut && f.shortcut === e.key.toLowerCase())
    if (!match) return
    e.preventDefault()
    format(match.run)
  }

  return (
    <form onSubmit={submit}>
      <div className="relative rounded-lg border-[1px] border-slate-300 bg-white transition focus-within:border-slate-400 focus-within:shadow-sm">
        <div className="flex items-center gap-0.5 px-1.5 pt-1.5" onMouseDown={(e) => e.preventDefault()}>
          {FORMATS.map((group, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="mx-1 h-4 w-px shrink-0 bg-slate-200" />}
              {group.map(f => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => format(f.run)}
                  title={f.shortcut ? `${f.label} (${SHORTCUT}${f.shortcut.toUpperCase()})` : f.label}
                  className={ICON}
                >
                  <FontAwesomeIcon icon={f.icon} />
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>

        <textarea
          ref={textarea}
          value={body}
          rows={3}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={(e) => uploader.current?.onPaste(e)}
          onDrop={(e) => uploader.current?.onDrop(e)}
          placeholder="Point concerné, écran, compte utilisé, résultat observé et résultat attendu"
          className="block max-h-[200px] w-full overflow-y-auto border-0 bg-transparent px-3 pb-1 pt-2 font-sans text-[15px] leading-6 text-slate-900 shadow-none outline-none placeholder:text-slate-400 focus:outline-none focus:ring-0"
        />

        <div className="flex items-center gap-1 px-1.5 pb-1.5">
          <Uploader
            ref={uploader}
            onUpload={onUpload}
            onLoadingChange={setUploading}
            multiple
            className={clsx(ICON, 'rounded-full bg-slate-100 [&:has(.dot-elastic)]:hidden')}
          >
            <span title="Ajouter un fichier, ou collez une capture d’écran"><FontAwesomeIcon icon="plus" /></span>
          </Uploader>
          {busy && (
            <span className="flex h-[28px] w-[28px] items-center justify-center text-[14px] text-brand-600" title="Ajout du fichier…">
              <FontAwesomeIcon icon="spinner" spin />
            </span>
          )}
          <span className="ml-1 truncate text-[12px] text-slate-400">Publié sur GitHub au nom de {currentName}</span>
          <button
            type="submit"
            title={`Publier (${SHORTCUT}Entrée)`}
            disabled={posting || busy || body.trim() === ''}
            className="ml-auto flex h-[34px] w-[42px] shrink-0 items-center justify-center rounded-md border-0 bg-brand-600 text-[16px] text-white transition hover:bg-brand-700 focus:outline-none disabled:cursor-default disabled:bg-white/0 disabled:text-slate-400"
          >
            <FontAwesomeIcon icon={posting ? 'spinner' : 'paper-plane'} spin={posting} />
          </button>
        </div>
      </div>
      {notice && <p className="m-0 mt-2 text-[12.5px] text-slate-600">{notice}</p>}
    </form>
  )
}

export default CommentForm
